import { useState, useEffect } from 'react';
import { Plus, Trash2, ArrowLeft, Save, Pencil, Images, FileText, Eye, Download } from 'lucide-react';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { CompactTable } from "../../components/ui/CompactTable";
import { api } from '../../services/api';
import { useConfirm } from '../../hooks/use-confirm';
import { toast } from 'sonner';
import { TableFilterBar } from '../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../hooks/useTableFilterState';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { API_BASE_URL } from '../../config/api';
import { DocumentAttachmentsCard } from '../../modules/legacy-core/components/DocumentAttachmentsCard';
import { DocumentPreviewDialog } from '../../modules/legacy-core/components/DocumentPreviewDialog';
import type { DocumentAttachment } from '../../services/legacyApi';

const COST_PROFILE_OPTIONS = ['GRC', 'GRP', 'BOM'] as const;
const ITEM_TYPE_OPTIONS = [
    { value: 'Stock', label: 'Stock / Storable', hint: 'Tracked in inventory quantity, WAC, valuation, and stock ledger.', className: 'bg-blue-50 text-blue-700' },
    { value: 'Consumable', label: 'Consumable / Expense', hint: 'Can be received for audit/billing but does not increase stock or valuation.', className: 'bg-orange-50 text-orange-700' },
    { value: 'Service', label: 'Service', hint: 'Non-physical service or labour. No stock movement.', className: 'bg-purple-50 text-purple-700' },
    { value: 'FixedAsset', label: 'Fixed Asset', hint: 'Classification only for now. Blocked from stock movement.', className: 'bg-slate-100 text-slate-700' },
] as const;

const normalizeItemType = (value?: string | null) => {
    const key = String(value || 'Stock').trim().toLowerCase().replace(/[-_\s]/g, '');
    if (key === 'consumable' || key === 'consumables' || key === 'nonstock' || key === 'expense') return 'Consumable';
    if (key === 'service' || key === 'services') return 'Service';
    if (key === 'fixedasset' || key === 'asset') return 'FixedAsset';
    return 'Stock';
};

const itemTypeMeta = (value?: string | null) => ITEM_TYPE_OPTIONS.find((option) => option.value === normalizeItemType(value)) || ITEM_TYPE_OPTIONS[0];

const documentAssetUrl = (url?: string | null) => {
    const value = String(url || '').trim();
    if (!value) return '';
    if (/^(https?:|blob:|data:)/i.test(value)) return value;
    if (API_BASE_URL.startsWith('/') && (value === API_BASE_URL || value.startsWith(`${API_BASE_URL}/`))) return value;
    return `${API_BASE_URL}${value.startsWith('/') ? value : `/${value}`}`;
};

// Simple Tab Component
const Tabs = ({ active, onChange, options }: { active: string, onChange: (v: string) => void, options: { id: string, label: string }[] }) => (
    <div className="flex space-x-1 border-b mb-6">
        {options.map(opt => (
            <button
                key={opt.id}
                onClick={() => onChange(opt.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${active === opt.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                    }`}
            >
                {opt.label}
            </button>
        ))}
    </div>
);

export function MaterialsManager() {
    const [activeTab, setActiveTab] = useState('all');
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({ q: '' });

    // Edit/Create State
    const [isEditing, setIsEditing] = useState(false);
    const [newItem, setNewItem] = useState<any>({});
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editContextTab, setEditContextTab] = useState<'material' | 'fg'>('material');
    const [categories, setCategories] = useState<any[]>([]);
    const [units, setUnits] = useState<any[]>([]);

    // Cost editing state (for GRC, GRP, BOM)
    const [editCost, setEditCost] = useState<any>(null);
    const [editType, setEditType] = useState<string>('');
    const [profileEditorOpen, setProfileEditorOpen] = useState(false);
    const [profileMaterial, setProfileMaterial] = useState<any>(null);
    const [profileForm, setProfileForm] = useState<any>({ cost_profiles: [], profile_defaults: {} });
    const [documentModal, setDocumentModal] = useState<null | {
        mode: 'gallery' | 'specs';
        entity: 'material' | 'fg';
        id: number;
        title: string;
    }>(null);
    const [modalDocuments, setModalDocuments] = useState<DocumentAttachment[]>([]);
    const [modalDocumentsLoading, setModalDocumentsLoading] = useState(false);
    const [modalUploadFile, setModalUploadFile] = useState<File | null>(null);
    const [modalUploadNote, setModalUploadNote] = useState('');
    const [previewDocument, setPreviewDocument] = useState<DocumentAttachment | null>(null);

    const { confirm } = useConfirm();

    const fetchData = async () => {
        setLoading(true);
        try {
            let res;
            if (activeTab === 'all') res = await api.materials.list();
            else if (activeTab === 'services') {
                const allMaterials = await api.materials.list();
                res = allMaterials.filter((m: any) => normalizeItemType(m.item_type) === 'Service');
            }
            else if (activeTab === 'consumables') {
                const allMaterials = await api.materials.list();
                res = allMaterials.filter((m: any) => normalizeItemType(m.item_type) === 'Consumable');
            }
            else if (activeTab === 'assets') {
                const allMaterials = await api.materials.list();
                res = allMaterials.filter((m: any) => normalizeItemType(m.item_type) === 'FixedAsset');
            }
            else if (activeTab === 'fg') res = await api.materials.listFinishedGoods();
            else if (activeTab === 'grc') res = await api.materials.listGRC();
            else if (activeTab === 'grp') res = await api.materials.listGRP();
            else if (activeTab === 'bom') res = await api.materials.listBOM();
            setData(res);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const fetchLookups = async () => {
        try {
            const [cats, units] = await Promise.all([
                api.masterData.list('tbl_material_categories'),
                api.masterData.list('tbl_units')
            ]);
            setCategories(cats);
            setUnits(units);
        } catch (e) {
            console.error("Error fetching lookups", e);
        }
    };

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    useEffect(() => {
        if (isEditing) fetchLookups();
    }, [isEditing]);

    const documentCategory = (modal: typeof documentModal) => modal?.mode === 'gallery' ? 'PRODUCT_IMAGE' : 'SPECIFICATION';

    const loadModalDocuments = async (modal = documentModal) => {
        if (!modal) return;
        setModalDocumentsLoading(true);
        try {
            const category = documentCategory(modal);
            const rows = modal.entity === 'fg'
                ? await api.materials.listFinishedGoodDocuments(modal.id, category)
                : await api.materials.listMaterialDocuments(modal.id, category);
            setModalDocuments(Array.isArray(rows) ? rows : []);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load documents');
            setModalDocuments([]);
        } finally {
            setModalDocumentsLoading(false);
        }
    };

    useEffect(() => {
        if (documentModal) {
            setModalUploadFile(null);
            setModalUploadNote('');
            loadModalDocuments(documentModal);
        }
    }, [documentModal]);

    const openGalleryModal = (entity: 'material' | 'fg', row: any) => {
        setDocumentModal({
            mode: 'gallery',
            entity,
            id: Number(entity === 'fg' ? row.item_id : row.material_id),
            title: entity === 'fg' ? (row.name || row.code || 'Finished Good') : (row.material_name || 'Material'),
        });
    };

    const openSpecsModal = (entity: 'material' | 'fg', row: any) => {
        setDocumentModal({
            mode: 'specs',
            entity,
            id: Number(entity === 'fg' ? row.item_id : row.material_id),
            title: entity === 'fg' ? (row.name || row.code || 'Finished Good') : (row.material_name || 'Material'),
        });
    };

    const modalPreviewUrl = (doc: DocumentAttachment) => {
        if (!documentModal) return '';
        return documentModal.entity === 'fg'
            ? api.materials.previewFinishedGoodDocument(documentModal.id, doc.doc_id)
            : api.materials.previewMaterialDocument(documentModal.id, doc.doc_id);
    };

    const downloadModalDocument = async (docId: number) => {
        if (!documentModal) return;
        if (documentModal.entity === 'fg') {
            await api.materials.downloadFinishedGoodDocument(documentModal.id, docId);
        } else {
            await api.materials.downloadMaterialDocument(documentModal.id, docId);
        }
    };

    const setCurrentModalDocument = async (docId: number) => {
        if (!documentModal) return;
        if (documentModal.entity === 'fg') {
            await api.materials.setFinishedGoodDocumentCurrent(documentModal.id, docId);
        } else {
            await api.materials.setMaterialDocumentCurrent(documentModal.id, docId);
        }
        await loadModalDocuments();
        fetchData();
    };

    const deleteModalDocument = async (docId: number) => {
        if (!documentModal) return;
        const ok = await confirm('Delete this document?', { variant: 'destructive', confirmText: 'Delete', cancelText: 'Cancel' });
        if (!ok) return;
        if (documentModal.entity === 'fg') {
            await api.materials.deleteFinishedGoodDocument(documentModal.id, docId);
        } else {
            await api.materials.deleteMaterialDocument(documentModal.id, docId);
        }
        await loadModalDocuments();
        fetchData();
    };

    const uploadModalDocument = async () => {
        if (!documentModal || !modalUploadFile) return;
        const payload = {
            file: modalUploadFile,
            category: documentCategory(documentModal),
            note: modalUploadNote.trim() || undefined,
            mark_current: true,
        };
        if (documentModal.entity === 'fg') {
            await api.materials.uploadFinishedGoodDocument(documentModal.id, payload);
        } else {
            await api.materials.uploadMaterialDocument(documentModal.id, payload);
        }
        setModalUploadFile(null);
        setModalUploadNote('');
        await loadModalDocuments();
        fetchData();
    };

    const sanitizeProfileDefaults = (rawDefaults: any, selectedProfiles: string[]) => {
        const selected = new Set((selectedProfiles || []).map((p) => String(p).toUpperCase()));
        const defaults: any = {};

        if (selected.has('GRC')) {
            defaults.GRC = {
                packing: String(rawDefaults?.GRC?.packing || '').trim(),
                saudi_cost: Number(rawDefaults?.GRC?.saudi_cost || 0),
                aed_cost: Number(rawDefaults?.GRC?.aed_cost || 0),
                usd_cost: Number(rawDefaults?.GRC?.usd_cost || 0),
                conversion_ratio: Number(rawDefaults?.GRC?.conversion_ratio || 0.045),
            };
        }
        if (selected.has('GRP')) {
            defaults.GRP = {
                saudi_cost: Number(rawDefaults?.GRP?.saudi_cost || 0),
                aed_cost: Number(rawDefaults?.GRP?.aed_cost || 0),
                usd_cost: Number(rawDefaults?.GRP?.usd_cost || 0),
                area_coverage: Number(rawDefaults?.GRP?.area_coverage || 0),
            };
        }
        if (selected.has('BOM')) {
            defaults.BOM = {
                unit_rate: Number(rawDefaults?.BOM?.unit_rate || 0),
                unit_weight: Number(rawDefaults?.BOM?.unit_weight || 0),
            };
        }
        return defaults;
    };

    const toggleProfile = (value: string, current: string[]) => {
        const normalized = String(value).toUpperCase();
        const exists = current.includes(normalized);
        if (exists) {
            return current.filter((p) => p !== normalized);
        }
        return [...current, normalized];
    };

    const normalizeUomConversions = (rawConversions: any[], baseUnitId: number) => {
        if (!Array.isArray(rawConversions)) return [];
        return rawConversions
            .map((row) => ({
                from_unit_id: Number(row?.from_unit_id || 0),
                factor_to_base: Number(row?.factor_to_base || 0),
                is_active: row?.is_active !== false,
            }))
            .filter((row) => row.from_unit_id > 0 && row.from_unit_id !== baseUnitId && row.factor_to_base > 0);
    };

    const openProfileEditor = async (row: any) => {
        setProfileMaterial(row);
        setProfileEditorOpen(true);
        try {
            const payload = await api.materials.getMaterialCostProfiles(row.material_id);
            const selected: string[] = Array.isArray(payload?.cost_profiles) ? payload.cost_profiles : [];
            setProfileForm({
                cost_profiles: selected,
                profile_defaults: {
                    GRC: {
                        packing: payload?.profiles?.GRC?.packing ?? '',
                        saudi_cost: payload?.profiles?.GRC?.saudi_cost ?? 0,
                        aed_cost: payload?.profiles?.GRC?.aed_cost ?? 0,
                        usd_cost: payload?.profiles?.GRC?.usd_cost ?? 0,
                        conversion_ratio: payload?.profiles?.GRC?.conversion_ratio ?? 0.045,
                    },
                    GRP: {
                        saudi_cost: payload?.profiles?.GRP?.saudi_cost ?? 0,
                        aed_cost: payload?.profiles?.GRP?.aed_cost ?? 0,
                        usd_cost: payload?.profiles?.GRP?.usd_cost ?? 0,
                        area_coverage: payload?.profiles?.GRP?.area_coverage ?? 0,
                    },
                    BOM: {
                        unit_rate: payload?.profiles?.BOM?.unit_rate ?? 0,
                        unit_weight: payload?.profiles?.BOM?.unit_weight ?? 0,
                    },
                },
            });
        } catch (error) {
            toast.error('Failed to load cost profiles');
            setProfileForm({
                cost_profiles: Array.isArray(row.cost_profiles) ? row.cost_profiles : [],
                profile_defaults: {},
            });
        }
    };

    const saveProfileEditor = async () => {
        if (!profileMaterial?.material_id) return;
        try {
            const selected = (profileForm.cost_profiles || []).map((p: string) => String(p).toUpperCase());
            await api.materials.updateMaterialCostProfiles(profileMaterial.material_id, {
                cost_profiles: selected,
                profile_defaults: sanitizeProfileDefaults(profileForm.profile_defaults, selected),
            });
            toast.success('Cost profiles updated');
            setProfileEditorOpen(false);
            setProfileMaterial(null);
            setProfileForm({ cost_profiles: [], profile_defaults: {} });
            fetchData();
        } catch (error) {
            toast.error('Failed to update cost profiles');
        }
    };

    const handleSave = async () => {
        try {
            if (editContextTab === 'fg') {
                const payload: any = {
                    name: String(newItem.name || '').trim(),
                    uom: String(newItem.uom || '').trim(),
                    category: String(newItem.category || 'Finished Goods').trim(),
                    description: newItem.description || null,
                    item_type: 'Stock',
                    active: true,
                };
                const explicitCode = String(newItem.code || '').trim();
                // Create: code is auto-generated by backend. Edit: preserve current code.
                if (editingId && explicitCode) {
                    payload.code = explicitCode;
                }
                if (editingId) {
                    await api.materials.updateFinishedGood(editingId, payload);
                } else {
                    await api.materials.createFinishedGood(payload);
                }
            } else {
                // Ensure IDs are numbers
                const selectedProfiles = (newItem.cost_profiles || []).map((p: string) => String(p).toUpperCase());
                const payload = {
                    ...newItem,
                    material_category_id: parseInt(newItem.material_category_id),
                    unit_id: parseInt(newItem.unit_id),
                    supplier_id: newItem.supplier_id ? parseInt(newItem.supplier_id) : null,
                    item_type: newItem.item_type || 'Stock',
                    cost_profiles: selectedProfiles,
                    profile_defaults: sanitizeProfileDefaults(newItem.profile_defaults, selectedProfiles),
                    uom_conversions: normalizeUomConversions(newItem.uom_conversions || [], parseInt(newItem.unit_id)),
                };
                if (editingId) {
                    await api.materials.update(editingId, payload);
                } else {
                    await api.materials.create(payload);
                }
            }
            setIsEditing(false);
            setNewItem({});
            setEditingId(null);
            fetchData();
        } catch (e) {
            console.error(e);
            toast.error(editContextTab === 'fg' ? "Failed to save finished good" : "Failed to save material");
        }
    };

    const handleDelete = async (id: number) => {
        const ok = await confirm('Are you sure? This might delete related cost data.', { variant: 'destructive', confirmText: 'Delete', cancelText: 'Cancel' });
        if (!ok) return;
        try {
            await api.materials.delete(id);
            toast.success('Material deleted');
            fetchData();
        } catch (e) {
            toast.error("Failed to delete");
        }
    };

    const filteredData = data.filter(item =>
        JSON.stringify(item).toLowerCase().includes(state.q.toLowerCase())
    );
    const uomOptions = Array.from(new Set((units || []).map((u: any) => String(u.unit || '').trim()).filter(Boolean)));
    const categoryOptions = Array.from(new Set((categories || []).map((c: any) => String(c.material_category || '').trim()).filter(Boolean)));

    // Column definitions for different tabs
    const allColumns = [
        {
            header: 'Image',
            accessor: (row: any) => {
                const image = row.primary_image;
                if (image?.preview_url) {
                    return (
                        <img
                            src={documentAssetUrl(image.preview_url)}
                            alt={`${row.material_name || 'Material'} primary image`}
                            className="h-10 w-10 rounded border object-cover"
                        />
                    );
                }
                return <div className="h-10 w-10 rounded border bg-slate-100" />;
            },
            className: 'w-14',
        },
        { header: 'ID', accessor: (row: any) => `M-${String(row.material_id).padStart(4, '0')}`, className: 'text-xs font-mono text-slate-500' },
        { header: 'Name', accessor: (row: any) => row.material_name, className: 'font-medium text-slate-700' },
        { header: 'Description', accessor: (row: any) => row.description, className: 'text-slate-600 text-xs truncate max-w-[200px]' },
        { header: 'Unit', accessor: (row: any) => row.unit, className: 'text-slate-600' },
        {
            header: 'Category', accessor: (row: any) => (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                    {row.material_category}
                </span>
            ), className: 'text-slate-600'
        },
        {
            header: 'Type',
            accessor: (row: any) => {
                const meta = itemTypeMeta(row.item_type);
                return (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${meta.className}`}>
                    {meta.label}
                </span>
                );
            },
            className: 'text-center'
        },
        {
            header: 'Cost Profiles',
            accessor: (row: any) => {
                const profiles = Array.isArray(row.cost_profiles) ? row.cost_profiles : [];
                if (!profiles.length) return <span className="text-xs text-slate-400">None</span>;
                return (
                    <div className="flex flex-wrap gap-1">
                        {profiles.map((profile: string) => (
                            <span key={`${row.material_id}-${profile}`} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                                {profile}
                            </span>
                        ))}
                    </div>
                );
            },
        },
        {
            header: '', accessor: (row: any) => (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={`Manage images for ${row.material_name || 'material'}`}
                        onClick={() => openGalleryModal('material', row)}
                    >
                        <Images className="w-4 h-4 text-emerald-700" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={`Specifications for ${row.material_name || 'material'}`}
                        onClick={() => openSpecsModal('material', row)}
                    >
                        <FileText className="w-4 h-4 text-slate-600" />
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        className="h-8 px-2 text-xs"
                        onClick={() => openProfileEditor(row)}
                    >
                        Manage Profiles
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                            setEditContextTab('material');
                            setEditingId(row.material_id);
                            setNewItem({
                                material_id: row.material_id,
                                material_name: row.material_name,
                                description: row.description,
                                material_category_id: row.material_category_id,
                                unit_id: row.unit_id,
                                supplier_id: row.supplier_id ?? null,
                                item_type: row.item_type || 'Stock',
                                cost_profiles: Array.isArray(row.cost_profiles) ? row.cost_profiles : [],
                                uom_conversions: Array.isArray(row.uom_conversions)
                                    ? row.uom_conversions
                                        .filter((conv: any) => !conv?.is_base)
                                        .map((conv: any) => ({
                                            from_unit_id: Number(conv?.from_unit_id || 0),
                                            factor_to_base: Number(conv?.factor_to_base || 0),
                                            is_active: conv?.is_active !== false,
                                        }))
                                    : [],
                            });
                            setIsEditing(true);
                        }}
                    >
                        <Pencil className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleDelete(row.material_id)}
                    >
                        <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                </div>
            ), className: 'text-right min-w-[300px] overflow-visible'
        }
    ];

    const fgColumns = [
        {
            header: 'Image',
            accessor: (row: any) => {
                const image = row.primary_image;
                if (image?.preview_url) {
                    return (
                        <img
                            src={documentAssetUrl(image.preview_url)}
                            alt={`${row.name || 'Finished good'} primary image`}
                            className="h-10 w-10 rounded border object-cover"
                        />
                    );
                }
                return <div className="h-10 w-10 rounded border bg-slate-100" />;
            },
            className: 'w-14',
        },
        { header: 'Code', accessor: (row: any) => row.code || '-', className: 'text-xs font-mono text-slate-600' },
        { header: 'Name', accessor: (row: any) => row.name || '-', className: 'font-medium text-slate-700' },
        { header: 'Description', accessor: (row: any) => row.description || '-', className: 'text-slate-600 text-xs truncate max-w-[220px]' },
        { header: 'UOM', accessor: (row: any) => row.uom || '-', className: 'text-slate-600' },
        {
            header: 'Type', accessor: () => (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                    Finished Good
                </span>
            )
        },
        {
            header: 'Category', accessor: (row: any) => (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                    {row.category || 'Finished Goods'}
                </span>
            )
        },
        { header: 'Stock', accessor: (row: any) => Number(row.current_stock || 0).toFixed(2), className: 'text-right font-mono' },
        { header: 'WAC', accessor: (row: any) => Number(row.wac_rate || 0).toFixed(2), className: 'text-right font-mono' },
        {
            header: '', accessor: (row: any) => (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={`Manage gallery for ${row.name || 'finished good'}`}
                        onClick={() => openGalleryModal('fg', row)}
                    >
                        <Images className="w-4 h-4 text-emerald-700" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={`Specifications for ${row.name || 'finished good'}`}
                        onClick={() => openSpecsModal('fg', row)}
                    >
                        <FileText className="w-4 h-4 text-slate-600" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                            setEditContextTab('fg');
                            setEditingId(Number(row.item_id));
                            setNewItem({
                                code: row.code,
                                name: row.name,
                                description: row.description,
                                uom: row.uom,
                                category: row.category || 'Finished Goods',
                                item_type: 'Stock'
                            });
                            setIsEditing(true);
                        }}
                    >
                        <Pencil className="w-4 h-4 text-blue-600" />
                    </Button>
                </div>
            ), className: 'text-right min-w-[144px] overflow-visible'
        }
    ];

    const grcColumns = [
        { header: 'ID', accessor: (row: any) => `M-${String(row.material_id).padStart(4, '0')}`, className: 'text-xs font-mono text-slate-500' },
        { header: 'Name', accessor: (row: any) => row.material_name, className: 'font-medium text-slate-700' },
        { header: 'Description', accessor: (row: any) => row.description, className: 'text-slate-600 text-xs truncate max-w-[200px]' },
        { header: 'Unit', accessor: (row: any) => row.unit, className: 'text-slate-600' },
        { header: 'Packing', accessor: (row: any) => row.packing, className: 'text-slate-600' },
        { header: 'Cost / SAR', accessor: (row: any) => Number(row.saudi_cost).toFixed(2), className: 'text-right font-mono' },
        { header: 'Cost / AED', accessor: (row: any) => Number(row.aed_cost).toFixed(2), className: 'text-right font-mono' },
        { header: 'Cost / USD', accessor: (row: any) => Number(row.usd_cost).toFixed(2), className: 'text-right font-mono' },
        { header: 'Conv. Ratio', accessor: (row: any) => Number(row.conversion_ratio).toFixed(3), className: 'text-right font-mono' },
        {
            header: '', accessor: (row: any) => (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        className="h-8 px-2 text-xs"
                        onClick={() => handleEditCost(row, activeTab)}
                    >
                        Edit
                    </Button>
                </div>
            ), className: 'text-right min-w-[88px] overflow-visible'
        }
    ];

    const grpColumns = [
        { header: 'ID', accessor: (row: any) => `M-${String(row.material_id).padStart(4, '0')}`, className: 'text-xs font-mono text-slate-500' },
        { header: 'Name', accessor: (row: any) => row.material_name, className: 'font-medium text-slate-700' },
        { header: 'Description', accessor: (row: any) => row.description, className: 'text-slate-600 text-xs truncate max-w-[200px]' },
        { header: 'Unit', accessor: (row: any) => row.unit, className: 'text-slate-600' },
        { header: 'Saudi Cost', accessor: (row: any) => Number(row.saudi_cost).toFixed(2), className: 'text-right font-mono' },
        { header: 'AED Cost', accessor: (row: any) => Number(row.aed_cost).toFixed(2), className: 'text-right font-mono' },
        { header: 'Area Coverage', accessor: (row: any) => Number(row.area_coverage).toFixed(3), className: 'text-right font-mono' },
        {
            header: '', accessor: (row: any) => (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        className="h-8 px-2 text-xs"
                        onClick={() => handleEditCost(row, activeTab)}
                    >
                        Edit
                    </Button>
                </div>
            ), className: 'text-right min-w-[88px] overflow-visible'
        }
    ];

    const bomColumns = [
        { header: 'ID', accessor: (row: any) => `M-${String(row.material_id).padStart(4, '0')}`, className: 'text-xs font-mono text-slate-500' },
        { header: 'Name', accessor: (row: any) => row.material_name, className: 'font-medium text-slate-700' },
        { header: 'Description', accessor: (row: any) => row.description, className: 'text-slate-600 text-xs truncate max-w-[200px]' },
        { header: 'Unit', accessor: (row: any) => row.unit, className: 'text-slate-600' },
        { header: 'Unit Rate', accessor: (row: any) => Number(row.unit_rate).toFixed(2), className: 'text-right font-mono' },
        { header: 'Unit Weight', accessor: (row: any) => Number(row.unit_weight).toFixed(4), className: 'text-right font-mono' },
        {
            header: '', accessor: (row: any) => (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        className="h-8 px-2 text-xs"
                        onClick={() => handleEditCost(row, activeTab)}
                    >
                        Edit
                    </Button>
                </div>
            ), className: 'text-right min-w-[88px] overflow-visible'
        }
    ];

    // --- FORM VIEW ---
    if (isEditing) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => { setIsEditing(false); setNewItem({}); setEditingId(null); }}>
                        <ArrowLeft className="w-5 h-5 text-slate-500" />
                    </Button>
                    <h2 className="text-xl font-semibold text-slate-800">
                        {editContextTab === 'fg'
                            ? (editingId ? 'Edit Finished Good' : 'Add Finished Good')
                            : (editingId ? 'Edit Material' : 'Add New Material')}
                    </h2>
                </div>

                <Card className="max-w-2xl">
                    <CardHeader>
                        <CardTitle>{editContextTab === 'fg' ? 'Finished Good Details' : 'Material Details'}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {editContextTab === 'fg' ? (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">FG Code</label>
                                        <Input
                                            aria-label="FG Code"
                                            value={newItem.code || ''}
                                            placeholder={editingId ? '' : 'Auto-generated on save'}
                                            disabled
                                        />
                                        {!editingId && (
                                            <p className="text-xs text-slate-500">Code will be generated automatically in format `FG-0001`.</p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">UOM</label>
                                        <select
                                            aria-label="UOM"
                                            className="w-full p-2 border rounded-md text-sm bg-background"
                                            value={newItem.uom || ''}
                                            onChange={e => setNewItem({ ...newItem, uom: e.target.value })}
                                        >
                                            <option value="">Select UOM...</option>
                                            {(newItem.uom && !uomOptions.includes(String(newItem.uom))) && (
                                                <option value={newItem.uom}>{newItem.uom} (legacy)</option>
                                            )}
                                            {uomOptions.map((unit) => (
                                                <option key={unit} value={unit}>{unit}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                        <label className="text-sm font-medium">Name</label>
                                        <Input aria-label="Name" value={newItem.name || ''} onChange={e => setNewItem({ ...newItem, name: e.target.value })} />
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                        <label className="text-sm font-medium">Category</label>
                                        <select
                                            aria-label="Category"
                                            className="w-full p-2 border rounded-md text-sm bg-background"
                                            value={newItem.category || ''}
                                            onChange={e => setNewItem({ ...newItem, category: e.target.value })}
                                        >
                                            <option value="">Select Category...</option>
                                            {(newItem.category && !categoryOptions.includes(String(newItem.category))) && (
                                                <option value={newItem.category}>{newItem.category} (legacy)</option>
                                            )}
                                            {categoryOptions.map((category) => (
                                                <option key={category} value={category}>{category}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                        <label className="text-sm font-medium">Description</label>
                                        <Input aria-label="Description" value={newItem.description || ''} onChange={e => setNewItem({ ...newItem, description: e.target.value })} />
                                    </div>
                                </div>
                                <div className="flex gap-3 pt-4 border-t">
                                    <Button onClick={handleSave} disabled={!newItem.name || !newItem.uom || !newItem.category}>
                                        <Save className="w-4 h-4 mr-2" /> {editingId ? 'Update Finished Good' : 'Save Finished Good'}
                                    </Button>
                                    <Button variant="outline" onClick={() => { setIsEditing(false); setNewItem({}); setEditingId(null); }}>
                                        Cancel
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2 col-span-2">
                                        <label className="text-sm font-medium">Material Name</label>
                                        <Input
                                            aria-label="Material Name"
                                            value={newItem.material_name || ''}
                                            onChange={e => setNewItem({ ...newItem, material_name: e.target.value })}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Category</label>
                                        <select
                                            aria-label="Material Category"
                                            className="w-full p-2 border rounded-md text-sm bg-background"
                                            value={newItem.material_category_id || ''}
                                            onChange={e => setNewItem({ ...newItem, material_category_id: e.target.value })}
                                        >
                                            <option value="">Select Category...</option>
                                            {categories.map(c => (
                                                <option key={c.material_category_id} value={c.material_category_id}>
                                                    {c.material_category}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Unit</label>
                                        <select
                                            aria-label="Material Unit"
                                            className="w-full p-2 border rounded-md text-sm bg-background"
                                            value={newItem.unit_id || ''}
                                            onChange={e => setNewItem({ ...newItem, unit_id: e.target.value })}
                                        >
                                            <option value="">Select Unit...</option>
                                            {units.map(u => (
                                                <option key={u.unit_id} value={u.unit_id}>
                                                    {u.unit}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Item Type</label>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {ITEM_TYPE_OPTIONS.map((option) => (
                                                <label key={option.value} className="flex items-start gap-2 cursor-pointer rounded-md border p-2 hover:bg-slate-50">
                                                    <input
                                                        type="radio"
                                                        name="item_type"
                                                        value={option.value}
                                                        checked={normalizeItemType(newItem.item_type) === option.value}
                                                        onChange={() => setNewItem({ ...newItem, item_type: option.value })}
                                                        className="mt-1 w-4 h-4"
                                                    />
                                                    <span>
                                                        <span className="block text-sm font-medium">{option.label}</span>
                                                        <span className="block text-xs text-muted-foreground">{option.hint}</span>
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            Only Stock/Storable items affect stock quantity, WAC, stock ledger, and inventory valuation.
                                        </p>
                                    </div>

                                    <div className="space-y-3 col-span-2 rounded-md border p-3 bg-slate-50/50">
                                        <label className="text-sm font-medium">UOM Conversions</label>
                                        <p className="text-xs text-muted-foreground">
                                            Base inventory UOM is the selected Unit above. Add alternate transaction UOMs only where conversion is required.
                                        </p>
                                        <div className="space-y-3">
                                            {(Array.isArray(newItem.uom_conversions) ? newItem.uom_conversions : []).map((row: any, index: number) => (
                                                <div key={`uom-${index}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end rounded border bg-white p-3">
                                                    <div className="md:col-span-6 space-y-1">
                                                        <label className="text-xs text-slate-500">Alternate UOM</label>
                                                        <select
                                                            className="w-full p-2 border rounded-md text-sm bg-background"
                                                            value={row?.from_unit_id || ''}
                                                            onChange={(e) => setNewItem((prev: any) => {
                                                                const next = Array.isArray(prev.uom_conversions) ? [...prev.uom_conversions] : [];
                                                                next[index] = { ...next[index], from_unit_id: e.target.value };
                                                                return { ...prev, uom_conversions: next };
                                                            })}
                                                        >
                                                            <option value="">Select alternate UOM...</option>
                                                            {units
                                                                .filter((u) => String(u.unit_id) !== String(newItem.unit_id || ''))
                                                                .map((u) => (
                                                                    <option key={u.unit_id} value={u.unit_id}>{u.unit}</option>
                                                                ))}
                                                        </select>
                                                    </div>
                                                    <div className="md:col-span-4 space-y-1">
                                                        <label className="text-xs text-slate-500">Factor to Base</label>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            step="0.000001"
                                                            value={row?.factor_to_base || ''}
                                                            onChange={(e) => setNewItem((prev: any) => {
                                                                const next = Array.isArray(prev.uom_conversions) ? [...prev.uom_conversions] : [];
                                                                next[index] = { ...next[index], factor_to_base: e.target.value };
                                                                return { ...prev, uom_conversions: next };
                                                            })}
                                                        />
                                                    </div>
                                                    <div className="md:col-span-2">
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            className="w-full"
                                                            onClick={() => setNewItem((prev: any) => ({
                                                                ...prev,
                                                                uom_conversions: (Array.isArray(prev.uom_conversions) ? prev.uom_conversions : []).filter((_: any, idx: number) => idx !== index),
                                                            }))}
                                                        >
                                                            <Trash2 className="w-4 h-4 text-red-600" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex justify-start">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                disabled={!newItem.unit_id}
                                                onClick={() => setNewItem((prev: any) => ({
                                                    ...prev,
                                                    uom_conversions: [
                                                        ...(Array.isArray(prev.uom_conversions) ? prev.uom_conversions : []),
                                                        { from_unit_id: '', factor_to_base: '' },
                                                    ],
                                                }))}
                                            >
                                                <Plus className="w-4 h-4 mr-2" />
                                                Add Alternate UOM
                                            </Button>
                                        </div>
                                    </div>

                                    {!editingId && (
                                        <div className="space-y-3 col-span-2 rounded-md border p-3 bg-slate-50/50">
                                            <label className="text-sm font-medium">Cost Profiles</label>
                                            <p className="text-xs text-muted-foreground">
                                                Cost Profiles control where this material appears: GRC, GRP, and/or BOM tabs.
                                            </p>
                                            <div className="flex flex-wrap gap-4">
                                                {COST_PROFILE_OPTIONS.map((profile) => {
                                                    const selectedProfiles: string[] = Array.isArray(newItem.cost_profiles) ? newItem.cost_profiles : [];
                                                    const checked = selectedProfiles.includes(profile);
                                                    return (
                                                        <label key={profile} className="flex items-center gap-2 cursor-pointer text-sm">
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() => setNewItem((prev: any) => ({
                                                                    ...prev,
                                                                    cost_profiles: toggleProfile(profile, Array.isArray(prev.cost_profiles) ? prev.cost_profiles : []),
                                                                }))}
                                                            />
                                                            <span>{profile}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>

                                            {(Array.isArray(newItem.cost_profiles) ? newItem.cost_profiles : []).includes('GRC') && (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-md border p-3">
                                                    <p className="text-xs font-medium text-slate-600 col-span-2">GRC Defaults</p>
                                                    <Input
                                                        placeholder="Packing"
                                                        value={newItem.profile_defaults?.GRC?.packing || ''}
                                                        onChange={(e) => setNewItem((prev: any) => ({
                                                            ...prev,
                                                            profile_defaults: {
                                                                ...(prev.profile_defaults || {}),
                                                                GRC: { ...(prev.profile_defaults?.GRC || {}), packing: e.target.value },
                                                            },
                                                        }))}
                                                    />
                                                    <Input
                                                        type="number"
                                                        placeholder="AED Cost"
                                                        value={newItem.profile_defaults?.GRC?.aed_cost ?? ''}
                                                        onChange={(e) => setNewItem((prev: any) => ({
                                                            ...prev,
                                                            profile_defaults: {
                                                                ...(prev.profile_defaults || {}),
                                                                GRC: { ...(prev.profile_defaults?.GRC || {}), aed_cost: e.target.value },
                                                            },
                                                        }))}
                                                    />
                                                </div>
                                            )}

                                            {(Array.isArray(newItem.cost_profiles) ? newItem.cost_profiles : []).includes('GRP') && (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-md border p-3">
                                                    <p className="text-xs font-medium text-slate-600 col-span-2">GRP Defaults</p>
                                                    <Input
                                                        type="number"
                                                        placeholder="AED Cost"
                                                        value={newItem.profile_defaults?.GRP?.aed_cost ?? ''}
                                                        onChange={(e) => setNewItem((prev: any) => ({
                                                            ...prev,
                                                            profile_defaults: {
                                                                ...(prev.profile_defaults || {}),
                                                                GRP: { ...(prev.profile_defaults?.GRP || {}), aed_cost: e.target.value },
                                                            },
                                                        }))}
                                                    />
                                                    <Input
                                                        type="number"
                                                        placeholder="Area Coverage"
                                                        value={newItem.profile_defaults?.GRP?.area_coverage ?? ''}
                                                        onChange={(e) => setNewItem((prev: any) => ({
                                                            ...prev,
                                                            profile_defaults: {
                                                                ...(prev.profile_defaults || {}),
                                                                GRP: { ...(prev.profile_defaults?.GRP || {}), area_coverage: e.target.value },
                                                            },
                                                        }))}
                                                    />
                                                </div>
                                            )}

                                            {(Array.isArray(newItem.cost_profiles) ? newItem.cost_profiles : []).includes('BOM') && (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-md border p-3">
                                                    <p className="text-xs font-medium text-slate-600 col-span-2">BOM Defaults</p>
                                                    <Input
                                                        type="number"
                                                        placeholder="Unit Rate"
                                                        value={newItem.profile_defaults?.BOM?.unit_rate ?? ''}
                                                        onChange={(e) => setNewItem((prev: any) => ({
                                                            ...prev,
                                                            profile_defaults: {
                                                                ...(prev.profile_defaults || {}),
                                                                BOM: { ...(prev.profile_defaults?.BOM || {}), unit_rate: e.target.value },
                                                            },
                                                        }))}
                                                    />
                                                    <Input
                                                        type="number"
                                                        placeholder="Unit Weight"
                                                        value={newItem.profile_defaults?.BOM?.unit_weight ?? ''}
                                                        onChange={(e) => setNewItem((prev: any) => ({
                                                            ...prev,
                                                            profile_defaults: {
                                                                ...(prev.profile_defaults || {}),
                                                                BOM: { ...(prev.profile_defaults?.BOM || {}), unit_weight: e.target.value },
                                                            },
                                                        }))}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {editingId && (
                                        <div className="col-span-2 text-xs text-muted-foreground rounded-md border p-3 bg-slate-50/50">
                                            Cost profile membership is managed from <strong>All Definitions</strong> using the <strong>Manage Profiles</strong> action.
                                        </div>
                                    )}

                                    <div className="space-y-2 col-span-2">
                                        <label className="text-sm font-medium">Description</label>
                                        <Input
                                            aria-label="Material Description"
                                            value={newItem.description || ''}
                                            onChange={e => setNewItem({ ...newItem, description: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-3 pt-4 border-t">
                                    <Button onClick={handleSave} disabled={!newItem.material_name || !newItem.material_category_id || !newItem.unit_id}>
                                        <Save className="w-4 h-4 mr-2" /> {editingId ? 'Update Material' : 'Save Material'}
                                    </Button>
                                    <Button variant="outline" onClick={() => { setIsEditing(false); setNewItem({}); setEditingId(null); }}>
                                        Cancel
                                    </Button>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        );
    }

    // --- EDIT DIALOG ---

    const handleEditCost = (item: any, type: string) => {
        setEditCost({ ...item }); // Copy item
        setEditType(type);
    };

    const saveCost = async () => {
        try {
            console.log("Saving cost for type:", editType, "Data:", editCost);
            if (editType === 'grc') {
                const id = parseInt(String(editCost.grc_material_id));
                if (isNaN(id)) throw new Error("Invalid GRC Material ID");

                await api.materials.updateGRC(id, {
                    packing: editCost.packing,
                    saudi_cost: parseFloat(editCost.saudi_cost || 0),
                    aed_cost: parseFloat(editCost.aed_cost || 0),
                    usd_cost: parseFloat(editCost.usd_cost || 0),
                    conversion_ratio: parseFloat(editCost.conversion_ratio || 0.045)
                });
            } else if (editType === 'grp') {
                const id = parseInt(String(editCost.grp_material_id));
                await api.materials.updateGRP(id, {
                    saudi_cost: parseFloat(editCost.saudi_cost || 0),
                    aed_cost: parseFloat(editCost.aed_cost || 0),
                    usd_cost: parseFloat(editCost.usd_cost || 0),
                    area_coverage: parseFloat(editCost.area_coverage || 0)
                });
            } else if (editType === 'bom') {
                const id = parseInt(String(editCost.bom_material_id));
                await api.materials.updateBOM(id, {
                    unit_rate: parseFloat(editCost.unit_rate || 0),
                    unit_weight: parseFloat(editCost.unit_weight || 0)
                });
            }
            console.log("Save successful");
            setEditCost(null);
            fetchData();
        } catch (e) {
            console.error("Save failed", e);
            toast.error("Failed to update cost: " + String(e));
        }
    };

    // --- LIST VIEW ---
    return (
        <div className="h-full flex flex-col relative">
            {/* Edit DIALOG OVERLAY */}
            {editCost && (
                <div className="absolute inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <Card className="w-full max-w-md shadow-xl animate-in zoom-in-95">
                        <CardHeader>
                            <CardTitle>Edit Cost Details</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="font-medium text-slate-700 border-b pb-2 mb-2">
                                <span className="mr-2 text-slate-500 text-sm font-mono">M-{String(editCost.material_id).padStart(4, '0')}</span>
                                {editCost.material_name}
                            </div>

                            {editType === 'grc' && (
                                <>
                                    <div className="space-y-1"><label className="text-xs">Packing</label><Input value={editCost.packing || ''} onChange={e => setEditCost({ ...editCost, packing: e.target.value })} /></div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-1"><label className="text-xs">Saudi Cost</label><Input type="number" value={editCost.saudi_cost} onChange={e => setEditCost({ ...editCost, saudi_cost: e.target.value })} /></div>
                                        <div className="space-y-1"><label className="text-xs">AED Cost</label><Input type="number" value={editCost.aed_cost} onChange={e => setEditCost({ ...editCost, aed_cost: e.target.value })} /></div>
                                        <div className="space-y-1"><label className="text-xs">USD Cost</label><Input type="number" value={editCost.usd_cost} onChange={e => setEditCost({ ...editCost, usd_cost: e.target.value })} /></div>
                                        <div className="space-y-1"><label className="text-xs">Conv. Ratio</label><Input type="number" value={editCost.conversion_ratio} onChange={e => setEditCost({ ...editCost, conversion_ratio: e.target.value })} /></div>
                                    </div>
                                </>
                            )}
                            {editType === 'grp' && (
                                <>
                                    <div className="space-y-1"><label className="text-xs">Saudi Cost</label><Input type="number" value={editCost.saudi_cost} onChange={e => setEditCost({ ...editCost, saudi_cost: e.target.value })} /></div>
                                    <div className="space-y-1"><label className="text-xs">AED Cost</label><Input type="number" value={editCost.aed_cost} onChange={e => setEditCost({ ...editCost, aed_cost: e.target.value })} /></div>
                                    <div className="space-y-1"><label className="text-xs">Area Coverage</label><Input type="number" value={editCost.area_coverage} onChange={e => setEditCost({ ...editCost, area_coverage: e.target.value })} /></div>
                                </>
                            )}
                            {editType === 'bom' && (
                                <>
                                    <div className="space-y-1"><label className="text-xs">Unit Rate</label><Input type="number" value={editCost.unit_rate} onChange={e => setEditCost({ ...editCost, unit_rate: e.target.value })} /></div>
                                    <div className="space-y-1"><label className="text-xs">Unit Weight</label><Input type="number" value={editCost.unit_weight} onChange={e => setEditCost({ ...editCost, unit_weight: e.target.value })} /></div>
                                </>
                            )}

                            <div className="flex justify-end gap-2 pt-4">
                                <Button variant="outline" onClick={() => setEditCost(null)}>Cancel</Button>
                                <Button onClick={saveCost}>Save Changes</Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {profileEditorOpen && (
                <div className="absolute inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <Card className="w-full max-w-lg shadow-xl animate-in zoom-in-95">
                        <CardHeader>
                            <CardTitle>Manage Cost Profiles</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="text-sm text-slate-600">
                                <span className="font-medium text-slate-800">{profileMaterial?.material_name || 'Material'}</span>
                                <p className="text-xs text-slate-500 mt-1">Profile membership controls which costing tabs include this material.</p>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                {COST_PROFILE_OPTIONS.map((profile) => {
                                    const selected: string[] = Array.isArray(profileForm.cost_profiles) ? profileForm.cost_profiles : [];
                                    const checked = selected.includes(profile);
                                    return (
                                        <label key={profile} className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => setProfileForm((prev: any) => ({
                                                    ...prev,
                                                    cost_profiles: toggleProfile(profile, Array.isArray(prev.cost_profiles) ? prev.cost_profiles : []),
                                                }))}
                                            />
                                            <span>{profile}</span>
                                        </label>
                                    );
                                })}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {(profileForm.cost_profiles || []).includes('GRC') && (
                                    <>
                                        <Input
                                            placeholder="GRC AED Cost"
                                            type="number"
                                            value={profileForm.profile_defaults?.GRC?.aed_cost ?? ''}
                                            onChange={(e) => setProfileForm((prev: any) => ({
                                                ...prev,
                                                profile_defaults: {
                                                    ...(prev.profile_defaults || {}),
                                                    GRC: { ...(prev.profile_defaults?.GRC || {}), aed_cost: e.target.value },
                                                },
                                            }))}
                                        />
                                        <Input
                                            placeholder="GRC Conversion Ratio"
                                            type="number"
                                            value={profileForm.profile_defaults?.GRC?.conversion_ratio ?? ''}
                                            onChange={(e) => setProfileForm((prev: any) => ({
                                                ...prev,
                                                profile_defaults: {
                                                    ...(prev.profile_defaults || {}),
                                                    GRC: { ...(prev.profile_defaults?.GRC || {}), conversion_ratio: e.target.value },
                                                },
                                            }))}
                                        />
                                    </>
                                )}
                                {(profileForm.cost_profiles || []).includes('GRP') && (
                                    <>
                                        <Input
                                            placeholder="GRP AED Cost"
                                            type="number"
                                            value={profileForm.profile_defaults?.GRP?.aed_cost ?? ''}
                                            onChange={(e) => setProfileForm((prev: any) => ({
                                                ...prev,
                                                profile_defaults: {
                                                    ...(prev.profile_defaults || {}),
                                                    GRP: { ...(prev.profile_defaults?.GRP || {}), aed_cost: e.target.value },
                                                },
                                            }))}
                                        />
                                        <Input
                                            placeholder="GRP Area Coverage"
                                            type="number"
                                            value={profileForm.profile_defaults?.GRP?.area_coverage ?? ''}
                                            onChange={(e) => setProfileForm((prev: any) => ({
                                                ...prev,
                                                profile_defaults: {
                                                    ...(prev.profile_defaults || {}),
                                                    GRP: { ...(prev.profile_defaults?.GRP || {}), area_coverage: e.target.value },
                                                },
                                            }))}
                                        />
                                    </>
                                )}
                                {(profileForm.cost_profiles || []).includes('BOM') && (
                                    <>
                                        <Input
                                            placeholder="BOM Unit Rate"
                                            type="number"
                                            value={profileForm.profile_defaults?.BOM?.unit_rate ?? ''}
                                            onChange={(e) => setProfileForm((prev: any) => ({
                                                ...prev,
                                                profile_defaults: {
                                                    ...(prev.profile_defaults || {}),
                                                    BOM: { ...(prev.profile_defaults?.BOM || {}), unit_rate: e.target.value },
                                                },
                                            }))}
                                        />
                                        <Input
                                            placeholder="BOM Unit Weight"
                                            type="number"
                                            value={profileForm.profile_defaults?.BOM?.unit_weight ?? ''}
                                            onChange={(e) => setProfileForm((prev: any) => ({
                                                ...prev,
                                                profile_defaults: {
                                                    ...(prev.profile_defaults || {}),
                                                    BOM: { ...(prev.profile_defaults?.BOM || {}), unit_weight: e.target.value },
                                                },
                                            }))}
                                        />
                                    </>
                                )}
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <Button variant="outline" onClick={() => { setProfileEditorOpen(false); setProfileMaterial(null); }}>
                                    Cancel
                                </Button>
                                <Button onClick={saveProfileEditor}>Save Profiles</Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            <DocumentPreviewDialog
                open={Boolean(previewDocument)}
                document={previewDocument}
                previewUrl={previewDocument ? modalPreviewUrl(previewDocument) : null}
                onOpenChange={(open) => !open && setPreviewDocument(null)}
                onDownload={(doc) => downloadModalDocument(doc.doc_id)}
            />

            <Dialog open={Boolean(documentModal)} onOpenChange={(open) => {
                if (!open) {
                    setDocumentModal(null);
                    setModalDocuments([]);
                }
            }}>
                <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {documentModal?.mode === 'gallery'
                                ? `Product Gallery - ${documentModal.title}`
                                : `${documentModal?.entity === 'fg' ? 'Finished Good' : 'Material'} Specifications - ${documentModal?.title || ''}`}
                        </DialogTitle>
                        <DialogDescription>
                            {documentModal?.mode === 'gallery'
                                ? 'Manage item images and choose the primary thumbnail image.'
                                : 'Manage specification files and preview PDFs or images without leaving this screen.'}
                        </DialogDescription>
                    </DialogHeader>

                    {documentModal?.mode === 'specs' ? (
                        <DocumentAttachmentsCard
                            title={documentModal.entity === 'fg' ? 'Finished Good Specifications' : 'Material Specifications'}
                            emptyLabel="No specification documents uploaded."
                            documents={modalDocuments}
                            loading={modalDocumentsLoading}
                            canUpload
                            canDownload
                            canDelete
                            onUpload={async (payload) => {
                                setModalUploadFile(payload.file);
                                setModalUploadNote(payload.note || '');
                                const activeModal = documentModal;
                                if (!activeModal) return;
                                const uploadPayload = {
                                    file: payload.file,
                                    category: 'SPECIFICATION',
                                    note: payload.note,
                                    mark_current: payload.markCurrent,
                                };
                                if (activeModal.entity === 'fg') {
                                    await api.materials.uploadFinishedGoodDocument(activeModal.id, uploadPayload);
                                } else {
                                    await api.materials.uploadMaterialDocument(activeModal.id, uploadPayload);
                                }
                                await loadModalDocuments(activeModal);
                            }}
                            onSetCurrent={setCurrentModalDocument}
                            onPreview={(doc) => setPreviewDocument(doc)}
                            onDownload={downloadModalDocument}
                            onDelete={deleteModalDocument}
                            onRefresh={() => loadModalDocuments()}
                        />
                    ) : (
                        <div className="space-y-4">
                            <div className="rounded border p-3">
                                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
                                    <input
                                        type="file"
                                        accept=".jpg,.jpeg,.png"
                                        className="text-sm"
                                        onChange={(e) => setModalUploadFile(e.target.files?.[0] || null)}
                                    />
                                    <Input
                                        placeholder="Caption / note (optional)"
                                        value={modalUploadNote}
                                        onChange={(e) => setModalUploadNote(e.target.value)}
                                    />
                                    <Button onClick={uploadModalDocument} disabled={!modalUploadFile}>
                                        <Plus className="h-4 w-4 mr-2" />
                                        Upload
                                    </Button>
                                </div>
                            </div>

                            {modalDocumentsLoading ? (
                                <div className="text-sm text-muted-foreground">Loading gallery...</div>
                            ) : modalDocuments.length === 0 ? (
                                <div className="text-sm text-muted-foreground">No product images uploaded.</div>
                            ) : (
                                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                                    {modalDocuments.map((doc) => (
                                        <div key={doc.doc_id} className="rounded border bg-white p-2">
                                            {doc.is_previewable ? (
                                                <button
                                                    type="button"
                                                    className="block aspect-square w-full overflow-hidden rounded border bg-slate-50"
                                                    onClick={() => setPreviewDocument(doc)}
                                                >
                                                    <img
                                                        src={documentAssetUrl(doc.preview_url || modalPreviewUrl(doc))}
                                                        alt={doc.original_file_name || 'Product image'}
                                                        className="h-full w-full object-cover"
                                                    />
                                                </button>
                                            ) : (
                                                <div className="flex aspect-square items-center justify-center rounded border bg-slate-50 text-xs text-slate-500">
                                                    No preview
                                                </div>
                                            )}
                                            <div className="mt-2 min-w-0">
                                                <div className="truncate text-xs font-medium">{doc.original_file_name || '-'}</div>
                                                {doc.is_current && <div className="text-xs text-emerald-700">Primary</div>}
                                            </div>
                                            <div className="mt-2 flex items-center gap-1">
                                                {!doc.is_current && (
                                                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setCurrentModalDocument(doc.doc_id)}>
                                                        Set Primary
                                                    </Button>
                                                )}
                                                <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Preview ${doc.original_file_name || 'image'}`} onClick={() => setPreviewDocument(doc)}>
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Download ${doc.original_file_name || 'image'}`} onClick={() => downloadModalDocument(doc.doc_id)}>
                                                    <Download className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Delete ${doc.original_file_name || 'image'}`} onClick={() => deleteModalDocument(doc.doc_id)}>
                                                    <Trash2 className="h-4 w-4 text-red-600" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            aria-label="Close specifications"
                            onClick={() => {
                                setDocumentModal(null);
                                setModalDocuments([]);
                            }}
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Controls */}
            <div className="flex items-center justify-between p-4 border-b bg-slate-50/50">
                <Tabs active={activeTab} onChange={setActiveTab} options={[
                    { id: 'all', label: 'All Definitions' },
                    { id: 'fg', label: 'Finished Goods' },
                    { id: 'consumables', label: 'Consumables' },
                    { id: 'services', label: 'Services' },
                    { id: 'assets', label: 'Fixed Assets' },
                    { id: 'grc', label: 'GRC Costing' },
                    { id: 'grp', label: 'GRP Costing' },
                    { id: 'bom', label: 'BOM Rates (Raw)' }
                ]} />
                <div className="flex gap-2">
                    {activeTab === 'all' && (
                        <Button onClick={() => { setEditContextTab('material'); setIsEditing(true); setEditingId(null); setNewItem({ cost_profiles: [], profile_defaults: {} }); }}>
                            <Plus className="w-4 h-4 mr-1" /> Add Material
                        </Button>
                    )}
                    {activeTab === 'fg' && (
                        <Button onClick={() => {
                            setEditContextTab('fg');
                            setIsEditing(true);
                            setEditingId(null);
                            setNewItem({ name: '', uom: '', category: '', description: '' });
                        }}>
                            <Plus className="w-4 h-4 mr-1" /> Add Finished Good
                        </Button>
                    )}
                    {activeTab === 'services' && (
                        <Button onClick={() => { setEditContextTab('material'); setIsEditing(true); setEditingId(null); setNewItem({ item_type: 'Service', cost_profiles: [], profile_defaults: {} }); }}>
                            <Plus className="w-4 h-4 mr-1" /> Add Service
                        </Button>
                    )}
                    {activeTab === 'consumables' && (
                        <Button onClick={() => { setEditContextTab('material'); setIsEditing(true); setEditingId(null); setNewItem({ item_type: 'Consumable', cost_profiles: [], profile_defaults: {} }); }}>
                            <Plus className="w-4 h-4 mr-1" /> Add Consumable
                        </Button>
                    )}
                    {activeTab === 'assets' && (
                        <Button onClick={() => { setEditContextTab('material'); setIsEditing(true); setEditingId(null); setNewItem({ item_type: 'FixedAsset', cost_profiles: [], profile_defaults: {} }); }}>
                            <Plus className="w-4 h-4 mr-1" /> Add Fixed Asset
                        </Button>
                    )}
                    {activeTab === 'grc' && (
                        <Button variant="secondary" onClick={() => {
                            const pigment = data.find(d => d.material_name.toLowerCase().includes('pigment'));
                            if (pigment) handleEditCost(pigment, 'grc');
                            else toast.error('Pigment material not found in this list');
                        }}>
                            Update Pigment Detail
                        </Button>
                    )}
                </div>
            </div>
            <div className="p-4 border-b">
                <TableFilterBar
                    searchValue={state.q}
                    onSearchChange={(value) => setFilter('q', value)}
                    searchPlaceholder={activeTab === 'fg' ? 'Search finished goods...' : 'Search materials...'}
                    isDirty={isDirty}
                    onClear={clearAll}
                    rightSlot={`${filteredData.length} results`}
                />
            </div>
            <div className="px-4 pt-2 text-xs text-muted-foreground">
                {activeTab === 'all' && 'All Definitions lists stock, consumable, service, and fixed asset material definitions.'}
                {activeTab === 'fg' && 'Finished Goods lists sellable/manufactured items (item_role = FG) with live stock and WAC from inventory.'}
                {activeTab === 'consumables' && 'Consumables can be received for audit and billing but are expensed, not stock-valued.'}
                {activeTab === 'services' && 'Services are non-stock labour/service definitions with no inventory movement.'}
                {activeTab === 'assets' && 'Fixed Assets are classification-only in this version and are blocked from stock movement.'}
                {activeTab === 'grc' && 'Membership driven by Cost Profiles: only materials with GRC profile are listed here.'}
                {activeTab === 'grp' && 'Membership driven by Cost Profiles: only materials with GRP profile are listed here.'}
                {activeTab === 'bom' && 'Membership driven by Cost Profiles: only materials with BOM profile are listed here.'}
            </div>
            <div className="flex-1 overflow-auto">
                {activeTab === 'all' && (
                    <CompactTable
                        data={filteredData}
                        columns={allColumns}
                        keyField="material_id"
                        loading={loading}
                        emptyMessage="No materials found"
                    />
                )}
                {activeTab === 'fg' && (
                    <CompactTable
                        data={filteredData}
                        columns={fgColumns}
                        keyField="item_id"
                        loading={loading}
                        emptyMessage="No finished goods found"
                    />
                )}
                {activeTab === 'grc' && (
                    <CompactTable
                        data={filteredData}
                        columns={grcColumns}
                        keyField="material_id"
                        loading={loading}
                        emptyMessage="No GRC materials found"
                    />
                )}
                {activeTab === 'grp' && (
                    <CompactTable
                        data={filteredData}
                        columns={grpColumns}
                        keyField="material_id"
                        loading={loading}
                        emptyMessage="No GRP materials found"
                    />
                )}
                {activeTab === 'bom' && (
                    <CompactTable
                        data={filteredData}
                        columns={bomColumns}
                        keyField="material_id"
                        loading={loading}
                        emptyMessage="No BOM materials found"
                    />
                )}
                {(activeTab === 'services' || activeTab === 'consumables' || activeTab === 'assets') && (
                    <CompactTable
                        data={filteredData}
                        columns={[
                            { header: 'ID', accessor: (row: any) => `M-${String(row.material_id).padStart(4, '0')}`, className: 'text-xs font-mono text-slate-500' },
                            { header: 'Name', accessor: (row: any) => row.material_name, className: 'font-medium text-slate-700' },
                            { header: 'Description', accessor: (row: any) => row.description, className: 'text-slate-600 text-xs' },
                            { header: 'Unit', accessor: (row: any) => row.unit, className: 'text-slate-600' },
                            {
                                header: 'Category', accessor: (row: any) => (
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${itemTypeMeta(row.item_type).className}`}>
                                        {row.material_category}
                                    </span>
                                ), className: 'text-slate-600'
                            },
                            {
                                header: 'Type', accessor: (row: any) => {
                                    const meta = itemTypeMeta(row.item_type);
                                    return <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${meta.className}`}>{meta.label}</span>;
                                }, className: 'text-center'
                            },
                            {
                                header: '', accessor: (row: any) => (
                                    <div className="flex items-center justify-end gap-2">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                            aria-label={`Specifications for ${row.material_name || 'material'}`}
                                            onClick={() => openSpecsModal('material', row)}
                                        >
                                            <FileText className="w-4 h-4 text-slate-600" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-8 w-8"
                                            onClick={() => {
                                                setEditContextTab('material');
                                                setEditingId(row.material_id);
                                                setNewItem({
                                                    material_id: row.material_id,
                                                    material_name: row.material_name,
                                                    description: row.description,
                                                    material_category_id: row.material_category_id,
                                                    unit_id: row.unit_id,
                                                    supplier_id: row.supplier_id ?? null,
                                                    item_type: normalizeItemType(row.item_type)
                                                });
                                                setIsEditing(true);
                                            }}
                                        >
                                            <Pencil className="w-4 h-4 text-blue-600" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-8 w-8"
                                            onClick={() => handleDelete(row.material_id)}
                                        >
                                            <Trash2 className="w-4 h-4 text-red-600" />
                                        </Button>
                                    </div>
                                ), className: 'text-right min-w-[132px] overflow-visible'
                            }
                        ]}
                        keyField="material_id"
                        loading={loading}
                        emptyMessage={`No ${activeTab.replace('_', ' ')} found.`}
                    />
                )}
            </div>
        </div>
    );
}
